/* eslint-disable react-refresh/only-export-components */
import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';

type ToastType = 'success' | 'error' | 'info' | 'warning';

interface ToastItem {
  id: number;
  message: string;
  type: ToastType;
}

let toastId = 0;
const listeners = new Set<(items: ToastItem[]) => void>();
let items: ToastItem[] = [];

function notify() {
  listeners.forEach((l) => l([...items]));
}

export function showToast(message: string, type: ToastType = 'info') {
  const id = ++toastId;
  items = [...items, { id, message, type }];
  notify();
  setTimeout(() => {
    items = items.filter((i) => i.id !== id);
    notify();
  }, 3000);
}

export function ToastContainer() {
  const [list, setList] = useState<ToastItem[]>([]);

  useEffect(() => {
    const cb = (newItems: ToastItem[]) => setList(newItems);
    listeners.add(cb);
    return () => { listeners.delete(cb); };
  }, []);

  const accentMap: Record<ToastType, string> = {
    success: 'bg-agrida-50 text-agrida-600',
    error: 'bg-red-50 text-red-500',
    info: 'bg-sky-50 text-sky-500',
    warning: 'bg-amber-50 text-amber-500',
  };

  const iconMap: Record<ToastType, string> = {
    success: 'ri-checkbox-circle-fill',
    error: 'ri-close-circle-fill',
    info: 'ri-information-fill',
    warning: 'ri-error-warning-fill',
  };

  // Portalled into <body> for the reason spelled out in ConfirmModal: a toast
  // trapped inside a page wrapper's stacking context can be painted under the
  // very sheet whose failure it is reporting, which reads as "no error message
  // at all" — exactly what was reported for a failed video upload.
  return createPortal(
    <div className="fixed inset-x-0 top-4 z-[10000] flex flex-col items-center gap-2 px-4 sm:items-end sm:right-4 sm:left-auto">
      {list.map((t) => (
        <div
          key={t.id}
          className="flex w-full max-w-[380px] items-center gap-2.5 rounded-2xl border border-slate-100 bg-white/95 px-3.5 py-3 text-slate-800 shadow-[0_8px_24px_-8px_rgba(15,23,42,0.18)] backdrop-blur-sm animate-[fadeIn_0.25s_ease-out]"
        >
          <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full ${accentMap[t.type]}`}>
            <i className={`${iconMap[t.type]} text-base`} />
          </span>
          <span className="text-[13px] font-medium leading-snug text-slate-700">{t.message}</span>
        </div>
      ))}
    </div>,
    document.body,
  );
}
